import { useEffect } from "react";
import { useLocale } from "@/i18n/LocaleProvider";
import type { Strings } from "@/i18n/strings";

/* Per-route title AND description, plus the social tags that mirror them.
 *
 * useDocumentTitle set only the title, so every route shared the homepage's
 * description and OG card no matter what it was about. This keeps the three
 * in step: what the tab says, what a search result says, and what a shared
 * link previews as are one decision, made per route.
 *
 * The static tags in index.html stay the Arabic default -- a crawler that
 * does not run JS still gets a correct, localized card for the default
 * locale. This updates them for everything that does.
 */

function setMeta(selector: string, attr: "name" | "property", key: string, value: string) {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}

export function useDocumentMeta(pick: (t: Strings) => { title: string; description: string }) {
  const { t, locale } = useLocale();
  const { title, description } = pick(t);

  useEffect(() => {
    document.title = title;
    setMeta('meta[name="description"]', "name", "description", description);

    const image = locale === "ar" ? "/brand/og-ar.png" : "/brand/og-en.png";
    setMeta('meta[property="og:title"]', "property", "og:title", title);
    setMeta('meta[property="og:description"]', "property", "og:description", description);
    setMeta('meta[property="og:image"]', "property", "og:image", image);
    setMeta('meta[property="og:locale"]', "property", "og:locale", locale === "ar" ? "ar_EG" : "en_GB");
    setMeta('meta[name="twitter:title"]', "name", "twitter:title", title);
    setMeta('meta[name="twitter:description"]', "name", "twitter:description", description);
    setMeta('meta[name="twitter:image"]', "name", "twitter:image", image);

    // The canonical URL is the current path without query or hash: filters and
    // panel tabs are the same document, not separate pages to index.
    let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", window.location.origin + window.location.pathname);
  }, [title, description, locale]);
}
